import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { ErpIntegrationProviderIdResource, ErpIntegrationResource } from 'Shared/util/types';
import { fetchErpIntegrationInvoicesSummary, generateSsoLink } from 'Shared/util/web-api';
import { useModal, useTranslation } from 'Shared/util/custom-hooks';
import useAppContext from 'Contexts/app-context';
import { formatAmount } from 'Shared/util/formatters';
import InformationModal from 'Shared/components/modals/information-modal';
import TextLink from 'Shared/components/text-link';
import Notice from 'Shared/components/notice';
import styles from './notice.css';


const getSvgPath = (providerId: ErpIntegrationProviderIdResource) => {
  switch (providerId) {
    case ErpIntegrationProviderIdResource.Acconomy:
      return '/integration/acconomy_logomark.svg';

    case ErpIntegrationProviderIdResource.Fortnox:
      return '/integration/fortnox_logomark.svg';

    default:
      return '';
  }
};

type InvoiceIntegrationNoticeProps = {
  erpIntegration: ErpIntegrationResource;
};

const InvoiceIntegrationNotice = ({
  erpIntegration,
}: InvoiceIntegrationNoticeProps) => {
  const [loading, setLoading] = useState(true);
  const [numberOfInvoices, setNumberOfInvoices] = useState(0);
  const [invoicesTotalAmount, setInvoicesTotalAmount] = useState(0);
  const [summaryError, setSummaryError] = useState(false);
  const [ssoLink, setSsoLink] = useState<string>();
  const history = useHistory();
  const { appData } = useAppContext();
  const { isModalOpen, openModal, closeModal } = useModal();
  const t = useTranslation(x => x.invoiceIntegration);
  const tg = useTranslation(x => x.global);

  useEffect(() => {
    /* NOTE: React might show an incorrect error ("Warning: Can't perform a React state update on an unmounted component...") if component
    have been unmounted on "setState". This should be fixed in upcoming release https://github.com/facebook/react/pull/22114 */
    Promise.allSettled([
      fetchErpIntegrationInvoicesSummary(erpIntegration.providerId),
      ...(erpIntegration.feature.singleSignOn ? [generateSsoLink({ path: 'invoices', providerId: erpIntegration.providerId })] : [])
    ])
      .then(([
        summaryResult,
        ssoLinkResult
      ]) => {
        if (summaryResult.status === 'fulfilled') {
          setNumberOfInvoices(summaryResult.value.totalNumberOfInvoices);
          setInvoicesTotalAmount(summaryResult.value.normalizedAmount);
        } else {
          setSummaryError(true);
        }

        if (ssoLinkResult?.status === 'fulfilled') {
          setSsoLink(ssoLinkResult.value);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const getButtonText = () => {
    if (erpIntegration.feature.invoiceImport) {
      return t.selectInvoices;
    }
    if (erpIntegration.feature.singleSignOn) {
      return t.format(t.go_to, { providerName: erpIntegration.providerName });
    }
    return '';
  };

  const handleButtonClick = () => {
    if (erpIntegration.feature.invoiceImport) {
      history.push('/invoice-integration');
    } else if (erpIntegration.feature.singleSignOn) {
      window.open(ssoLink);
    }
  };

  const hideButton = (summaryError && erpIntegration.feature.invoiceImport);

  return (
    <>
      <Notice
        svgPath={getSvgPath(erpIntegration.providerId)}
        content={summaryError
          ? t.format(t.thereWasProblemRetrievingInvoicesFrom, { providerName: erpIntegration.providerName })
          : numberOfInvoices < 1
            ? (
              <>
                {t.format(t.thereAreCurrentlyNoInvoicesToSellFrom, { providerName: erpIntegration.providerName })}
                {erpIntegration.providerId !== ErpIntegrationProviderIdResource.Acconomy &&
                  <TextLink text={t.areYouMissingInvoices} link={openModal} />
                }
              </>
            ) : (
              <>
                {t.format(t.thereIsNumberInvoicesOnTotal, {
                  number: numberOfInvoices,
                  invoices: (numberOfInvoices > 1 ? tg.invoicesPlural : tg.invoicesSingular)
                })}
                <span className={styles.largeAmountText}>{formatAmount(invoicesTotalAmount)}</span>
                {t.format(t.currencyInProviderWichIsReadyForSale, {
                  providerName: erpIntegration.providerName,
                  cultureCurrency: appData.currencyCode
                })}
              </>
            )
        }
        button={hideButton ? undefined : {
          text: getButtonText(),
          onClick: handleButtonClick,
          disabled: (erpIntegration.feature.singleSignOn && !ssoLink),
        }}
        loading={loading}
      />

      <InformationModal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        headerText={t.format(t.areYouMissingInvoicesFrom, { providerName: erpIntegration.providerName })}
        contentText={t.format(t.ifYouDoNotHaveInvoicesFrom, {
          providerName: erpIntegration.providerName,
          additionalContentText: erpIntegration.providerId === ErpIntegrationProviderIdResource.Fortnox
            ? t.onlyTheLast50invoicesAreDisplayes
            : ''
        })}
      />
    </>
  );
};

export default InvoiceIntegrationNotice;
